import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllInvoicesOptions,
	getAllInvoicesQueryKey,
} from "@/lib/client/@tanstack/react-query.gen";
import type { InvoiceWithParentDto } from "@/lib/client/types.gen";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable, DataTableColumnHeader } from "@/components/data-table";
import { normalizeString } from "@/lib/utils";
import { getSchoolLabel } from "@/stores/enum-store";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Receipt, CheckCircle, XCircle, Eye, FileText, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { confirmDraftInvoices, deleteDraftInvoices } from "@/lib/client";

export const Route = createFileRoute("/admin/invoices/preview")({
	component: InvoicePreviewPage,
});

function InvoicePreviewPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const { data: invoicesData, isLoading } = useQuery(getAllInvoicesOptions());
	const invoices = (invoicesData as any as InvoiceWithParentDto[]) || [];

	const draftInvoices = invoices
		.filter((inv) => inv.status === "DRAFT")
		.sort((a, b) => {
			const numA = a.invoiceNumber ? parseInt(a.invoiceNumber.replace(/\D/g, '')) : 0;
			const numB = b.invoiceNumber ? parseInt(b.invoiceNumber.replace(/\D/g, '')) : 0;
			return numA - numB;
		});

	const confirmDraft = useMutation({
		mutationFn: async () => {
			if (draftInvoices.length === 0) return;
			const period = draftInvoices[0].billingPeriodStart;
			if (!period) throw new Error("No billing period found");
			const yearMonth = period.substring(0, 7);
			const response = await confirmDraftInvoices({ query: { period: yearMonth } })
			return response.data;
		},
		onSuccess: (data: any) => {
			const count = Array.isArray(data) ? data.length : 0;
			toast.success(`${count} facture(s) confirmée(s) avec succès`, {
				description: "Les notifications ont été envoyées aux parents.",
			});
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
			navigate({ to: "/admin/invoices" });
		},
		onError: () => {
			toast.error("Erreur lors de la confirmation des factures");
		},
	});

	const deleteDraft = useMutation({
		mutationFn: async () => {
			if (draftInvoices.length === 0) return;
			const period = draftInvoices[0].billingPeriodStart;
			if (!period) throw new Error("No billing period found");
			const yearMonth = period.substring(0, 7);
			const response = await deleteDraftInvoices(  { query: { period: yearMonth } });
			return response.data;
		},
		onSuccess: () => {
			toast.success("Factures brouillon supprimées avec succès");
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
			navigate({ to: "/admin/invoices" });
		},
		onError: () => {
			toast.error("Erreur lors de la suppression des factures");
		},
	});

	const stats = {
		total: draftInvoices.length,
		totalAmount: draftInvoices.reduce(
			(acc, inv) => acc + (inv.totalAmount || 0),
			0,
		),
		zeroAmount: draftInvoices.filter((inv) => (inv.totalAmount || 0) === 0).length,
		nonZeroAmount: draftInvoices.filter((inv) => (inv.totalAmount || 0) > 0).length,
	};

	const columns = useMemo<ColumnDef<InvoiceWithParentDto>[]>(
		() => [
			{
				accessorKey: "invoiceNumber",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Numéro" />
				),
				cell: ({ row }) => {
					const invoice = row.original;
					return (
						<div className="flex items-center gap-2">
							<FileText className="h-4 w-4 text-gray-400" />
							<span className="font-medium">
								#{invoice.invoiceNumber}
							</span>
						</div>
					);
				},
				meta: { label: "Numéro" },
			},
			{
				id: "parent",
				accessorFn: (row) => (row.parent?.lastName || "") + " " + (row.parent?.firstName || ""),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => {
					const invoice = row.original;
					return (
						<div>
							<p className="font-medium">
								{invoice.parent?.firstName} {invoice.parent?.lastName}
							</p>
						</div>
					);
				},
				filterFn: (row, columnId, filterValue: string) => {
					const invoice = row.original;
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${invoice.parent?.firstName || ""} ${invoice.parent?.lastName || ""}`
					);
					const reverseName = normalizeString(
						`${invoice.parent?.lastName || ""} ${invoice.parent?.firstName || ""}`
					);
					return fullName.includes(searchValue) || reverseName.includes(searchValue);
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
            {
                id: "enfants",
                accessorFn: (row) => row.parent.id + "_enfants",
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title="Enfants" />
                ),
                cell: ({ row }) => {
                    const invoice = row.original;
                    return (
                        <div className="text-sm">
                            {invoice.parent?.students && invoice.parent.students.length > 0 ? (
                                invoice.parent.students.map((student: any, idx: number) => (
                                    <div key={idx} className="text-gray-700">
                                        {student.firstName} {student.lastName}
                                    </div>
                                ))
                            ) : (
                                <span className="text-gray-400">-</span>
                            )}
                        </div>
                    );
                },
                meta: {
                    label: "Enfants",
                    textValue: (row: InvoiceWithParentDto) => {
                        const students = row.parent?.students || [];
                        return students.map((s: any) => `${s.firstName} ${s.lastName}`).join(", ") || "-";
                    },
                },
            },
			{
				id: "etablissement",
				accessorFn: (row) => {
					const students = row.parent?.students || [];
					const schools = [...new Set(students.map((s: any) => getSchoolLabel(s.school)))];
					return schools.join(", ") || "-";
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Établissement" />
				),
				cell: ({ row }) => (
					<span className="text-sm text-gray-600">
						{row.getValue("etablissement")}
					</span>
				),
				meta: { label: "Établissement" },
			},
			{
				id: "period",
				accessorFn: (row) => {
					if (row.billingPeriodStart && row.billingPeriodEnd) {
						return `${format(new Date(row.billingPeriodStart), "dd/MM", { locale: fr })} - ${format(new Date(row.billingPeriodEnd), "dd/MM/yy", { locale: fr })}`;
					}
					return "Non définie";
				},
				header: "Période",
				cell: ({ row }) => (
					<span className="text-sm">
						{row.getValue("period")}
					</span>
				),
				enableSorting: false,
				meta: { label: "Période" },
			},
			{
				accessorKey: "totalAmount",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Montant" />
				),
				cell: ({ row }) => (
					<p className="font-medium">
						{row.original.totalAmount?.toFixed(2)} €
					</p>
				),
				meta: {
					label: "Montant",
					textValue: (row: InvoiceWithParentDto) => `${row.totalAmount?.toFixed(2)} €`,
				},
			},
			{
				accessorKey: "dueDate",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Échéance" />
				),
				cell: ({ row }) => {
					const invoice = row.original;
					return invoice.dueDate ? (
						<span className="text-sm">
							{format(new Date(invoice.dueDate), "dd/MM/yyyy", {
								locale: fr,
							})}
						</span>
					) : (
						"Non définie"
					);
				},
				meta: {
					label: "Échéance",
					textValue: (row: InvoiceWithParentDto) =>
						row.dueDate ? format(new Date(row.dueDate), "dd/MM/yyyy", { locale: fr }) : "Non définie",
				},
			},
			{
				id: "actions",
				header: "Actions",
				cell: ({ row }) => (
					<Button
						size="sm"
						variant="outline"
						onClick={() =>
							navigate({ to: `/admin/invoices/${row.original.id}` })
						}
					>
						<Eye className="h-3 w-3 mr-1" />
						Voir détails
					</Button>
				),
				enableSorting: false,
				meta: { label: "Actions" },
			},
		],
		[navigate],
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement des factures...</p>
				</div>
			</div>
		);
	}

	if (draftInvoices.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex justify-between items-center">
					<div>
						<h1 className="text-3xl font-bold">Preview des factures</h1>
						<p className="text-gray-600 mt-2">
							Aucune facture en mode brouillon à prévisualiser
						</p>
					</div>
					<Button onClick={() => navigate({ to: "/admin/invoices" })}>
						Retour
					</Button>
				</div>
				<Card>
					<CardContent className="py-12">
						<div className="text-center">
							<Receipt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
							<p className="text-gray-500 font-medium">
								Aucune facture brouillon trouvée
							</p>
							<p className="text-gray-400 text-sm mt-1">
								Générez d'abord des factures en mode preview
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Preview des factures</h1>
					<p className="text-gray-600 mt-2">
						Vérifiez les factures avant de les envoyer aux parents
					</p>
				</div>
				<div className="flex gap-2">
					<Button
						variant="outline"
						onClick={() => navigate({ to: "/admin/invoices" })}
					>
						Retour
					</Button>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="destructive">
								<XCircle className="mr-2 h-4 w-4" />
								Annuler et supprimer
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Êtes-vous sûr?</AlertDialogTitle>
								<AlertDialogDescription>
									Cette action supprimera toutes les {draftInvoices.length}{" "}
									factures brouillon. Vous devrez les régénérer si vous voulez
									les envoyer.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Annuler</AlertDialogCancel>
								<AlertDialogAction
									onClick={() => deleteDraft.mutate()}
									disabled={deleteDraft.isPending}
									className="bg-red-600 hover:bg-red-700"
								>
									{deleteDraft.isPending
										? "Suppression..."
										: "Supprimer les factures"}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button>
								<CheckCircle className="mr-2 h-4 w-4" />
								Confirmer et envoyer
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Confirmer l'envoi des factures</AlertDialogTitle>
								<AlertDialogDescription className="space-y-2">
									<p>
										Cette action va confirmer {draftInvoices.length}{" "}
										facture(s) pour un montant total de {stats.totalAmount.toFixed(2)} €.
									</p>
									{stats.zeroAmount > 0 && (
										<p className="text-orange-600">
											⚠️ {stats.zeroAmount} facture(s) à 0€ seront automatiquement annulées.
										</p>
									)}
									{stats.nonZeroAmount > 0 && (
										<p className="text-green-600">
											✓ Les parents des {stats.nonZeroAmount} facture(s) avec un montant &gt; 0€ recevront une notification par email et SMS.
										</p>
									)}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Annuler</AlertDialogCancel>
								<AlertDialogAction
									onClick={() => confirmDraft.mutate()}
									disabled={confirmDraft.isPending}
								>
									{confirmDraft.isPending
										? "Confirmation..."
										: "Confirmer et envoyer"}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>

			{/* Statistiques */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<Card className="border-yellow-200 bg-yellow-50">
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Factures en preview
						</CardTitle>
						<AlertTriangle className="h-4 w-4 text-yellow-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{stats.total}</div>
						<p className="text-xs text-muted-foreground">
							En attente de confirmation
						</p>
						{stats.zeroAmount > 0 && (
							<p className="text-xs text-gray-500 mt-1">
								({stats.zeroAmount} à 0€)
							</p>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Montant total</CardTitle>
						<Receipt className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{stats.totalAmount.toFixed(2)} €
						</div>
						<p className="text-xs text-muted-foreground">
							À envoyer aux parents
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Liste des factures */}
			<Card>
				<CardHeader>
					<CardTitle>Factures en mode brouillon</CardTitle>
					<CardDescription>
						Vérifiez chaque facture avant de confirmer l'envoi
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={draftInvoices}
						initialColumnVisibility={{ etablissement: false }}
						emptyMessage="Aucune facture brouillon"
					/>
				</CardContent>
			</Card>
		</div>
	);
}
